// Tabela responsiva (DESIGN §5.20): tabela real no lg (≥ 1024); lista de cards (painel único com linhas) abaixo.
// Cada coluna declara seu papel no mobile: primaria (título), badge (topo direito), meta (linha de apoio),
// acao (rodapé direito) ou oculta-mobile. Células com 14 de padding lateral, como nas pranchas (cabe a 1280;
// auditoria de fidelidade A-2), e a ação da linha sempre à vista (não só no hover), como nas pranchas.
import { Fragment, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { useDesktop } from "../lib/hooks";
import { EsqueletoLinha } from "./Esqueleto";
import { Icones } from "./icones";
import { ErroCarga } from "./Vazio";

export type PapelMobile = "primaria" | "badge" | "meta" | "acao" | "oculta-mobile";

export interface ColunaTabela<T> {
  id: string;
  titulo: string;
  /** Título só para leitor de tela (ex.: coluna de ações). */
  tituloOculto?: boolean;
  celula: (linha: T) => ReactNode;
  /**
   * Conteúdo no card do celular, quando difere da célula da tabela (ex.: Treinos › Modelos: a coluna "Montado por"
   * vira o badge de origem, e "Em uso" vira a linha "Marina Costa · 23 alunos · 29/09"). Padrão: `celula`.
   */
  celulaMobile?: (linha: T) => ReactNode;
  papelMobile: PapelMobile;
  /** Alinha à direita (números). */
  numerica?: boolean;
  /** Coluna ordenável: estado controlado por `ordenacao`/`aoOrdenar`. */
  ordenavel?: boolean;
  /**
   * Deixa o texto da célula quebrar linha. Padrão: não quebra (`white-space: nowrap`), como nas pranchas; use
   * só em coluna de texto livre (descrição, mensagem), onde duas linhas são melhores que uma tabela larga.
   */
  quebrar?: boolean;
  /**
   * A célula já traz o próprio controle que abre a linha (ex.: link no nome do aluno). Sem isto, a coluna
   * `primaria` de uma tabela com `aoClicarLinha` vira um botão (teclado: Tab + Enter/Espaço), porque a linha
   * clicável sozinha não recebe foco.
   */
  controleProprio?: boolean;
  className?: string;
}

export interface Ordenacao {
  coluna: string;
  direcao: "asc" | "desc";
}

export interface PropsTabela<T> {
  rotulo: string;
  colunas: ColunaTabela<T>[];
  linhas: T[];
  chave: (linha: T) => string;
  /** Avatar/ícone no início do card mobile. */
  inicioMobile?: (linha: T) => ReactNode;
  carregando?: boolean;
  erro?: boolean;
  aoTentarDeNovo?: () => void;
  /** Conteúdo quando vazio (Vazio com ação). */
  vazio?: ReactNode;
  ordenacao?: Ordenacao;
  aoOrdenar?: (o: Ordenacao) => void;
  /** Linha expansível (ex.: calendário 90 dias + últimas mensagens). */
  expandir?: (linha: T) => ReactNode;
  /**
   * No `lg`, expande clicando na linha (Enter/Espaço com foco nela), sem a coluna da seta, e a linha aberta ganha a
   * barra tinta à esquerda, como na prancha "Alunos em risco". `aoClicarLinha` fica sem efeito no desktop.
   */
  expandirNaLinha?: boolean;
  /**
   * Clique na linha/card (ex.: abrir ficha do aluno). Pelo teclado, o conteúdo da coluna `primaria` vira um botão
   * que faz o mesmo (a menos que ela tenha `controleProprio`); sem coluna assim, a própria linha recebe foco e abre
   * com Enter/Espaço.
   */
  aoClicarLinha?: (linha: T) => void;
  /** Barra acima (busca, chips, contagem, ações em lote). */
  ferramentas?: ReactNode;
  /** Rodapé ("1–50 de 312" + paginação ou "Carregar mais"). */
  rodape?: ReactNode;
  /** Linha de 60 (duas linhas de texto com avatar 32, ex.: Equipe). Padrão 56. */
  linhaAlta?: boolean;
  /** Linha de apoio do card no celular em `text-rotulo` (12) em vez de `text-apoio` (14). */
  metaMobileRotulo?: boolean;
  /** Linha de apoio do card no celular quebra em até duas linhas em vez de cortar com "…" (mockup de vídeo §5). */
  metaMobileQuebra?: boolean;
  className?: string;
}

export function Tabela<T>(props: PropsTabela<T>) {
  const desktop = useDesktop();
  return (
    <section
      aria-label={props.rotulo}
      // o anel vai num ::after por cima: a coluna de ação fixa (fundo painel) não o cobre no canto
      className={cn(
        "relative overflow-hidden rounded-card bg-painel",
        "after:pointer-events-none after:absolute after:inset-0 after:rounded-card after:shadow-anel after:content-['']",
        props.className,
      )}
    >
      {props.ferramentas && <div className="border-b border-fio p-4">{props.ferramentas}</div>}
      {props.erro ? (
        <ErroCarga texto="Não deu para carregar a lista." aoTentar={props.aoTentarDeNovo} />
      ) : props.carregando ? (
        <div aria-busy="true">
          <span className="sr-only">Carregando</span>
          <div className="divide-y divide-fio">
            {Array.from({ length: 8 }, (_, i) => (
              <EsqueletoLinha key={i} />
            ))}
          </div>
        </div>
      ) : props.linhas.length === 0 ? (
        props.vazio
      ) : desktop ? (
        <TabelaDesktop {...props} />
      ) : (
        <ListaCards {...props} />
      )}
      {props.rodape && <div className="border-t border-fio px-4 py-3">{props.rodape}</div>}
    </section>
  );
}

/** Conteúdo da coluna principal como botão real: o clique na linha também abre, mas só o botão recebe foco. */
function BotaoLinha({
  aoClicar,
  className,
  children,
}: {
  aoClicar: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn("cursor-pointer rounded-controle text-left", className)}
      onClick={(e) => {
        // a linha/card também escuta o clique: sem isto abriria duas vezes
        e.stopPropagation();
        aoClicar();
      }}
    >
      {children}
    </button>
  );
}

/** Coluna que vira o botão da linha (primeira `primaria` sem `controleProprio`), ou null. */
function colunaDoBotao<T>(colunas: ColunaTabela<T>[]): string | null {
  if (colunas.some((c) => c.controleProprio)) return null;
  return colunas.find((c) => c.papelMobile === "primaria")?.id ?? null;
}

/** Enter/Espaço com foco na própria linha (não num botão dentro dela). */
const teclaDaLinha = (acao: () => void) => (e: KeyboardEvent) => {
  if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
  e.preventDefault();
  acao();
};

function TabelaDesktop<T>({
  colunas,
  linhas,
  chave,
  ordenacao,
  aoOrdenar,
  expandir,
  aoClicarLinha,
  linhaAlta,
  expandirNaLinha,
}: PropsTabela<T>) {
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const seta = !!expandir && !expandirNaLinha;
  const expandeNaLinha = !!expandir && !!expandirNaLinha;
  const abre = !expandeNaLinha ? aoClicarLinha : undefined;
  const colunaBotao = abre ? colunaDoBotao(colunas) : null;
  // sem coluna que vire botão (nem controle próprio), a linha é o controle, como em "Alunos em risco"
  const linhaFocavel = expandeNaLinha || (!!abre && !colunaBotao && !colunas.some((c) => c.controleProprio));
  const alternar = (k: string) =>
    setAbertas((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    // Rede de segurança: se as colunas não couberem, a tabela rola dentro do painel em vez de ser cortada
    // pelo `overflow-hidden` do cartão (QA fidelidade A-2). O normal é caber.
    <div className="overflow-x-auto">
      <table className="w-full text-apoio">
        <thead>
          <tr className="h-10 border-b border-fio">
            {seta && <th scope="col" className="w-12" aria-label="Expandir" />}
            {colunas.map((c) => {
              const ativa = ordenacao?.coluna === c.id;
              const ariaSort = ativa
                ? ordenacao?.direcao === "asc"
                  ? "ascending"
                  : "descending"
                : undefined;
              return (
                <th
                  key={c.id}
                  scope="col"
                  aria-sort={c.ordenavel ? (ariaSort ?? "none") : undefined}
                  className={cn(
                    "px-3.5 text-left text-rotulo font-medium whitespace-nowrap text-texto-3",
                    c.numerica && "text-right",
                    c.papelMobile === "acao" && "sticky right-0 bg-painel",
                  )}
                >
                  {c.ordenavel && aoOrdenar ? (
                    <button
                      type="button"
                      // coluna da ordem escolhida em tinta/600 (mockup Treinos › Modelos, 30/09)
                      className={cn(
                        "inline-flex h-10 items-center gap-1 hover:text-tinta",
                        ativa && "font-semibold text-tinta",
                      )}
                      onClick={() =>
                        aoOrdenar({
                          coluna: c.id,
                          direcao: ativa && ordenacao?.direcao === "desc" ? "asc" : "desc",
                        })
                      }
                    >
                      {c.titulo}
                      <Icones.ordenar size={16} aria-hidden />
                    </button>
                  ) : c.tituloOculto || c.papelMobile === "acao" ? (
                    // coluna de ação: cabeçalho vazio como nas pranchas; o nome fica para o leitor de tela
                    <span className="sr-only">{c.titulo}</span>
                  ) : (
                    c.titulo
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const k = chave(l);
            const aberta = abertas.has(k);
            return (
              <Fragment key={k}>
                <tr
                  className={cn(
                    "group border-b border-fio last:border-b-0 hover:bg-recuo",
                    linhaAlta ? "h-15" : "h-14",
                    (abre || expandeNaLinha) && "cursor-pointer",
                    linhaFocavel &&
                      "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco",
                  )}
                  {...(expandeNaLinha
                    ? {
                        tabIndex: 0,
                        "aria-expanded": aberta,
                        onClick: () => alternar(k),
                        onKeyDown: teclaDaLinha(() => alternar(k)),
                      }
                    : abre
                      ? {
                          onClick: () => abre(l),
                          ...(linhaFocavel ? { tabIndex: 0, onKeyDown: teclaDaLinha(() => abre(l)) } : {}),
                        }
                      : {})}
                >
                  {seta && (
                    <td className="pl-2">
                      <button
                        type="button"
                        aria-expanded={aberta}
                        aria-label={aberta ? "Recolher" : "Expandir"}
                        onClick={(e) => {
                          e.stopPropagation();
                          alternar(k);
                        }}
                        className="grid size-10 place-items-center rounded-controle text-texto-2 hover:bg-fio"
                      >
                        <Icones.expandir
                          size={20}
                          aria-hidden
                          className={cn("transition-transform", aberta && "rotate-180")}
                        />
                      </button>
                    </td>
                  )}
                  {colunas.map((c) => (
                    <td
                      key={c.id}
                      className={cn(
                        "px-3.5",
                        !c.quebrar && "whitespace-nowrap",
                        c.numerica && "text-right",
                        // a ação fica presa à direita: se as colunas passarem da largura, o meio rola e a
                        // ação continua à vista (QA fidelidade A-2)
                        c.papelMobile === "acao" && "sticky right-0 bg-painel group-hover:bg-recuo",
                        // sticky cria contexto de empilhamento: com o menu ⋮ aberto, a célula sobe para o menu não
                        // ficar por baixo da ação da linha seguinte (achado no filtro "Sem vídeo")
                        c.papelMobile === "acao" && "has-[[aria-expanded=true]]:z-20",
                        c.className,
                      )}
                    >
                      {c.papelMobile === "acao" ? (
                        // o clique na ação não abre a linha
                        <span onClick={(e) => e.stopPropagation()}>{c.celula(l)}</span>
                      ) : abre && c.id === colunaBotao ? (
                        <BotaoLinha aoClicar={() => abre(l)} className="block max-w-full">
                          {c.celula(l)}
                        </BotaoLinha>
                      ) : (
                        c.celula(l)
                      )}
                    </td>
                  ))}
                </tr>
                {expandir && aberta && (
                  <tr className="border-b border-fio">
                    {expandirNaLinha ? (
                      // prancha: fundo do painel, barra tinta de 3 à esquerda, alinhado ao nome (16/16/18/62)
                      <td
                        colSpan={colunas.length}
                        className="bg-painel pt-4 pr-4 pb-[18px] pl-[62px] shadow-[inset_3px_0_var(--gs-tinta)]"
                      >
                        {expandir(l)}
                      </td>
                    ) : (
                      <td colSpan={colunas.length + 1} className="bg-recuo px-4 py-4">
                        {expandir(l)}
                      </td>
                    )}
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ListaCards<T>({
  colunas,
  linhas,
  chave,
  inicioMobile,
  expandir,
  aoClicarLinha,
  metaMobileRotulo,
  metaMobileQuebra,
}: PropsTabela<T>) {
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const por = (papel: PapelMobile) => colunas.filter((c) => c.papelMobile === papel);
  const primaria = por("primaria");
  const badge = por("badge");
  const meta = por("meta");
  const acao = por("acao");
  const colunaBotao = aoClicarLinha ? colunaDoBotao(colunas) : null;
  const noCard = (c: ColunaTabela<T>, l: T) => (c.celulaMobile ?? c.celula)(l);
  const cardFocavel = !!aoClicarLinha && !colunaBotao && !colunas.some((c) => c.controleProprio);

  return (
    <ul className="divide-y divide-fio">
      {linhas.map((l) => {
        const k = chave(l);
        const aberta = abertas.has(k);
        // meta vazia (null) não entra na linha de apoio nem deixa um " · " sobrando
        const metas = meta
          .map((c) => ({ id: c.id, conteudo: noCard(c, l) }))
          .filter((m) => m.conteudo !== null && m.conteudo !== undefined && m.conteudo !== false);
        return (
          <li key={k}>
            <div
              className={cn(
                "flex gap-3 px-4 py-3",
                aoClicarLinha && "cursor-pointer active:bg-recuo",
                cardFocavel &&
                  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco",
              )}
              onClick={aoClicarLinha ? () => aoClicarLinha(l) : undefined}
              {...(cardFocavel ? { tabIndex: 0, onKeyDown: teclaDaLinha(() => aoClicarLinha!(l)) } : {})}
            >
              {inicioMobile && <div className="shrink-0">{inicioMobile(l)}</div>}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-start justify-between gap-2">
                  {colunaBotao ? (
                    <BotaoLinha
                      aoClicar={() => aoClicarLinha!(l)}
                      className="min-w-0 truncate text-titulo text-tinta"
                    >
                      {primaria.map((c) => (
                        <Fragment key={c.id}>{noCard(c, l)}</Fragment>
                      ))}
                    </BotaoLinha>
                  ) : (
                    <div className="min-w-0 truncate text-titulo text-tinta">
                      {primaria.map((c) => (
                        <Fragment key={c.id}>{noCard(c, l)}</Fragment>
                      ))}
                    </div>
                  )}
                  <div className="flex shrink-0 gap-1">
                    {badge.map((c) => (
                      <Fragment key={c.id}>{noCard(c, l)}</Fragment>
                    ))}
                  </div>
                </div>
                {metas.length > 0 && (
                  <div
                    className={cn(
                      "flex flex-col text-texto-2",
                      metaMobileRotulo ? "text-rotulo" : "text-apoio",
                    )}
                  >
                    {/* até 2 colunas por linha, unidas por " · " */}
                    {Array.from({ length: Math.ceil(metas.length / 2) }, (_, i) => (
                      <span key={i} className={cn(!metaMobileQuebra && "truncate")}>
                        {metas[i * 2]?.conteudo}
                        {metas[i * 2 + 1] && <> · {metas[i * 2 + 1]!.conteudo}</>}
                      </span>
                    ))}
                  </div>
                )}
                {(acao.length > 0 || expandir) && (
                  <div
                    className="mt-1 flex items-center justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {expandir && (
                      <button
                        type="button"
                        aria-expanded={aberta}
                        onClick={() =>
                          setAbertas((s) => {
                            const n = new Set(s);
                            if (n.has(k)) n.delete(k);
                            else n.add(k);
                            return n;
                          })
                        }
                        className="mr-auto inline-flex h-toque items-center gap-1 text-apoio font-medium text-texto-2"
                      >
                        <Icones.expandir size={16} aria-hidden className={cn(aberta && "rotate-180")} />
                        {aberta ? "Menos" : "Detalhes"}
                      </button>
                    )}
                    {acao.map((c) => (
                      <Fragment key={c.id}>{noCard(c, l)}</Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {expandir && aberta && <div className="bg-recuo px-4 py-4">{expandir(l)}</div>}
          </li>
        );
      })}
    </ul>
  );
}
